import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { deckService, cardService, cardProgressService } from "@/services/firestore";
import { Flashcard, Deck, FlashcardProgress } from "@/types";
import { calculateNextReview, mapRatingToQuality, SRSData } from "@/lib/srs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Trophy,
  Volume2,
  VolumeX,
  BookOpen,
  ChevronRight,
  Info
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { speak, stopSpeech } from "@/lib/speech";
import { AssetCache } from "@/lib/cache";
import { useSettingsStore } from "@/store/settingsStore";
import StudySettings from "@/components/StudySettings";


export default function StudyPage() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuthStore();
  const navigate = useNavigate();

  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [progress, setProgress] = useState<Record<string, FlashcardProgress>>({});
  const [activeBoxIndex, setActiveBoxIndex] = useState(0);
  const [currentBoxQueue, setCurrentBoxQueue] = useState<Flashcard[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Study session state
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionResults, setSessionResults] = useState<{cardId: string, rating: string}[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Quiz Mode state
  const [studyMode, setStudyMode] = useState<'flashcard' | 'quiz'>('flashcard');
  const [quizOptions, setQuizOptions] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [flashcardChoice, setFlashcardChoice] = useState<string | null>(null);
  const { 
    autoReadFront,
    autoReadBack,
    frontLang, 
    backLang, 
    speechRate, 
    speechPitch, 
    speechVolume,
    shouldAutoRead
  } = useSettingsStore();

  const [direction] = useState<'forward' | 'backward'>('forward');



  // Helper to generate quiz options
  const generateQuizOptions = (card: Flashcard, allCards: Flashcard[]) => {
    if (!card) return [];
    const distractors = allCards
      .filter(c => c.id !== card.id)
      .map(c => c.back.text)
      .filter((value, index, self) => self.indexOf(value) === index) // Unique
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    
    return [card.back.text, ...distractors].sort(() => Math.random() - 0.5);
  };

  useEffect(() => {
    if (user && id) {
      const fetchData = async () => {
        try {
          const [deckData, cardsData, progressData] = await Promise.all([
            deckService.getDeck(id),
            cardService.getCardsByDeck(id),
            cardProgressService.getAllProgressForDeck(user.uid, id)
          ]);

          if (!deckData) {
            navigate("/decks");
            return;
          }

          setDeck(deckData);
          setCards(cardsData);
          
          const progressMap: Record<string, FlashcardProgress> = {};
          progressData.forEach(p => {
            progressMap[p.cardId] = p;
          });
          setProgress(progressMap);

          // Initialize first available box
          let firstBoxIndex = -1;
          for (let i = 1; i <= 5; i++) {
            const hasCards = cardsData.some(c => (progressMap[c.id!]?.boxIndex || 1) === i);
            if (hasCards) {
              firstBoxIndex = i - 1;
              break;
            }
          }

          if (firstBoxIndex !== -1) {
            setActiveBoxIndex(firstBoxIndex);
            const boxQueue = cardsData
              .filter(c => (progressMap[c.id!]?.boxIndex || 1) === firstBoxIndex + 1)
              .sort(() => Math.random() - 0.5);
            setCurrentBoxQueue(boxQueue);
            
            if (boxQueue.length > 0) {
              setQuizOptions(generateQuizOptions(boxQueue[0], cardsData));
            }
          }

          // Preload assets
          const assetUrls = cardsData.flatMap(c => [
            c.front.audio,
            c.back.audio,
            c.front.image,
            c.back.image
          ]).filter((url): url is string => !!url);
          AssetCache.preload(assetUrls);

        } catch (error) {
          console.error("Error fetching study data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [user, id, navigate]);

  const currentCard = currentBoxQueue[0];

  const readFront = useCallback(() => {
    if (!currentCard) return;
    speak(currentCard.front.text, { 
      lang: currentCard.front.lang || frontLang, 
      audio: currentCard.front.audio,
      rate: speechRate,
      pitch: speechPitch,
      volume: speechVolume
    });
  }, [currentCard, frontLang, speechRate, speechPitch, speechVolume]);

  const readBack = useCallback(() => {
    if (!currentCard) return;
    speak(currentCard.back.text, { 
      lang: currentCard.back.lang || backLang, 
      audio: currentCard.back.audio,
      rate: speechRate,
      pitch: speechPitch,
      volume: speechVolume
    });
  }, [currentCard, backLang, speechRate, speechPitch, speechVolume]);



  const handleRate = async (rating: 'again' | 'hard' | 'good' | 'easy') => {
    if (!user || !currentCard || !id || isTransitioning) return;

    setIsTransitioning(true);
    setFlashcardChoice(rating);
    const quality = mapRatingToQuality(rating);
    
    const currentProgress = progress[currentCard.id!];
    const prevSRS: SRSData = currentProgress ? {
      repetition: currentProgress.repetition,
      interval: currentProgress.interval,
      easeFactor: currentProgress.easeFactor,
      nextReview: currentProgress.nextReview?.toDate ? currentProgress.nextReview.toDate() : new Date(currentProgress.nextReview),
      boxIndex: currentProgress.boxIndex || 1
    } : {
      repetition: 0,
      interval: 0,
      easeFactor: 2.5,
      nextReview: new Date(),
      boxIndex: 1
    };

    const nextSRS = calculateNextReview(quality, prevSRS);

    const newProgress: FlashcardProgress = {
      userId: user.uid,
      deckId: id,
      cardId: currentCard.id!,
      boxIndex: nextSRS.boxIndex,
      repetition: nextSRS.repetition,
      interval: nextSRS.interval,
      easeFactor: nextSRS.easeFactor,
      nextReview: nextSRS.nextReview,
      updatedAt: new Date()
    };

    const updatedProgressMap = { ...progress, [currentCard.id!]: newProgress };
    setProgress(updatedProgressMap);
    setSessionResults(prev => [...prev, { cardId: currentCard.id!, rating }]);

    try {
      await cardProgressService.updateCardProgress(newProgress);
    } catch (error) {
      console.error("Failed to update card progress:", error);
    }

    const nextAction = () => {
      const remainingInBox = currentBoxQueue.slice(1);
      
      if (remainingInBox.length > 0) {
        setCurrentBoxQueue(remainingInBox);
        setQuizOptions(generateQuizOptions(remainingInBox[0], cards));
        setIsTransitioning(false);
        setIsFlipped(false);
        setFlashcardChoice(null);
        setSelectedOption(null);
      } else {
        // Find next box with cards based on updated progress
        let nextBox = -1;
        for (let i = 1; i <= 5; i++) {
          const checkIdx = (activeBoxIndex + i) % 5;
          const boxCards = cards.filter(c => (updatedProgressMap[c.id!]?.boxIndex || 1) === checkIdx + 1);
          if (boxCards.length > 0) {
            nextBox = checkIdx;
            break;
          }
        }

        if (nextBox !== -1) {
          setActiveBoxIndex(nextBox);
          const nextQueue = cards
            .filter(c => (updatedProgressMap[c.id!]?.boxIndex || 1) === nextBox + 1)
            .sort(() => Math.random() - 0.5);
          
          setCurrentBoxQueue(nextQueue);
          setQuizOptions(generateQuizOptions(nextQueue[0], cards));
          setIsTransitioning(false);
          setIsFlipped(false);
          setFlashcardChoice(null);
          setSelectedOption(null);
        } else {
          setIsTransitioning(false);
          setIsFinished(true);
        }
      }
    };

    if (studyMode === 'flashcard') {
      nextAction();
    } else {
      setTimeout(nextAction, 1200);
    }
  };

  const handleStart = () => {
    speak("", { volume: 0 }); 
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

        if (studyMode === 'quiz') {
            if (selectedOption || isTransitioning) return;
            const key = e.key;
            if (['1', '2', '3', '4'].includes(key)) {
                const idx = parseInt(key) - 1;
                if (quizOptions[idx]) {
                    const option = quizOptions[idx];
                    const isCorrect = option === currentCard?.back.text;
                    setSelectedOption(option);
                    setIsFlipped(true);
                    handleRate(isCorrect ? 'good' : 'again');
                }
            }
        } else if (studyMode === 'flashcard') {
            if (e.key === ' ' && !isTransitioning) {
                e.preventDefault();
                setIsFlipped(!isFlipped);
            }
            if (isFlipped && !isTransitioning) {
                if (e.key === '1') handleRate('again');
                if (e.key === '2') handleRate('good');
            }
        }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [studyMode, quizOptions, selectedOption, isTransitioning, currentCard, isFlipped, handleRate]);

  useEffect(() => {
    if (!currentCard) return;
    
    // Auto-read logic based on visible side
    if (isFlipped) {
      if (shouldAutoRead('back')) {
        readBack();
      }
    } else {
      // For front side, only auto-read if not in the middle of a card transition
      if (!isTransitioning && shouldAutoRead('front')) {
        readFront();
      }
    }
  }, [currentCard?.id, isFlipped, isTransitioning, readFront, readBack, shouldAutoRead]);



  useEffect(() => {
    return () => stopSpeech();
  }, []);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-svh w-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }


  if (cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-svh w-screen text-center max-w-md mx-auto px-4 bg-background">
        <div className="mb-6 p-6 rounded-full bg-muted">
          <Info className="h-10 w-10 text-muted-foreground" />
        </div>
        <h2 className="text-3xl font-black mb-4 uppercase tracking-tighter">Chưa có thẻ nào để học</h2>
        <Link to={`/decks/${id}`} className={cn(buttonVariants({ size: "lg", className: "rounded-2xl px-10 font-bold" }))}>
          Quay lại bộ thẻ
        </Link>
      </div>
    );
  }

  if (isFinished || !currentCard) {
    return (
      <div className="flex flex-col items-center justify-center h-svh w-screen text-center max-w-md mx-auto px-4 bg-background font-sans">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mb-8 p-10 rounded-full bg-primary/10 text-primary">
          <Trophy className="h-16 w-16" />
        </motion.div>
        <h2 className="text-4xl font-black mb-4 tracking-tighter italic uppercase underline decoration-primary decoration-8 offset-2">Hoàn thành!</h2>
        <p className="text-xl text-muted-foreground mb-10 font-medium">Bạn vừa ôn tập hoàn tất {sessionResults.length} thẻ kiến thức.</p>
        
        <div className="flex flex-col gap-3 w-full">
          <Button 
            size="lg" 
            className="w-full h-14 rounded-2xl text-lg font-bold shadow-lg shadow-primary/20 bg-primary"
            onClick={() => {
              const progressMap = { ...progress };
              let firstBoxIndex = -1;
              for (let i = 1; i <= 5; i++) {
                if (cards.some(c => (progressMap[c.id!]?.boxIndex || 1) === i)) { firstBoxIndex = i - 1; break; }
              }
              if (firstBoxIndex !== -1) {
                setActiveBoxIndex(firstBoxIndex);
                const boxQueue = cards.filter(c => (progressMap[c.id!]?.boxIndex || 1) === firstBoxIndex + 1).sort(() => Math.random() - 0.5);
                setCurrentBoxQueue(boxQueue);
                if (boxQueue.length > 0) setQuizOptions(generateQuizOptions(boxQueue[0], cards));
              }
              setIsFinished(false);
              setSessionResults([]);
              setIsFlipped(false);
              setSelectedOption(null);
              setIsTransitioning(false);
            }}
          >
            Học tập vòng lặp mới
          </Button>

          <Link to={`/decks/${id}`} className={cn(buttonVariants({ variant: "outline", size: "lg", className: "w-full h-14 rounded-2xl text-lg font-bold" }))}>
            Quay lại bềEthẻ
          </Link>
        </div>
      </div>
    );
  }

  const boxCounts = [0, 0, 0, 0, 0];
  cards.forEach(card => {
    const box = progress[card.id!]?.boxIndex || 1;
    boxCounts[box - 1]++;
  });

  const cardsInActiveBox = cards.filter(c => (progress[c.id!]?.boxIndex || 1) === activeBoxIndex + 1).length;
  const cardsFinishedInBox = cardsInActiveBox - currentBoxQueue.length + 1;

  return (
    <div className="flex flex-col h-svh w-screen bg-background overflow-hidden relative font-sans pb-4 md:pb-12">
      <Helmet><title>Học: {deck?.title || "Flashcard"}</title></Helmet>

      <div className="w-full pt-2 md:pt-10 px-4 md:px-10 flex-shrink-0">
        <div className="flex items-center justify-between mb-4 md:mb-8">
          <Link to={`/decks/${id}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-bold text-sm tracking-tight">
            <ArrowLeft className="h-4 w-4" /><span>QUAY LẠI</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex bg-muted p-1 rounded-xl items-center gap-1">
              <Button
                variant={studyMode === 'flashcard' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-lg h-8 px-3 text-[10px] font-black uppercase tracking-widest"
                onClick={() => setStudyMode('flashcard')}
              >Flashcard</Button>
              <Button
                variant={studyMode === 'quiz' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-lg h-8 px-3 text-[10px] font-black uppercase tracking-widest"
                onClick={() => { setStudyMode('quiz'); setQuizOptions(generateQuizOptions(currentCard, cards)); setIsFlipped(false); }}
              >Quiz</Button>
            </div>

            <StudySettings 
              trigger={
                <button 
                  className={cn(
                    "p-1.5 md:p-2 rounded-xl border transition-all", 
                    (autoReadFront || autoReadBack) ? "bg-primary/5 border-primary/20 text-primary" : "bg-muted/30 text-zinc-400"
                  )}
                >
                  {(autoReadFront || autoReadBack) ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
              }
            />


            <div className="flex flex-col items-end">
              <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">BOX {activeBoxIndex + 1}</span>
              <span className="text-xl font-black italic tabular-nums leading-none">{cardsFinishedInBox} / {cardsInActiveBox}</span>
            </div>
          </div>
        </div>

        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mt-4">
          <motion.div initial={{ width: 0 }} animate={{ width: `${(cardsFinishedInBox / cardsInActiveBox) * 100}%` }} className="h-full bg-primary" />
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row w-full max-w-full mx-auto px-4 md:px-6 min-h-0 gap-8 md:gap-12 relative">
        <div className="flex-1 relative min-h-0 flex flex-col items-center justify-center py-2 md:py-8 gap-4 md:gap-8">
          <div className="w-full max-w-xl grid grid-cols-5 gap-2 md:gap-3 px-2 md:px-0">
            {[1, 2, 3, 4, 5].map((boxNum) => (
              <div key={boxNum} className={cn("flex flex-col items-center justify-center p-1 md:p-1.5 rounded-xl border-2 transition-all", (activeBoxIndex + 1) === boxNum ? "bg-primary border-primary shadow-md" : "bg-muted/20 border-muted/50")}>
                <span className={cn("text-[6px] md:text-[8px] font-black uppercase", (activeBoxIndex + 1) === boxNum ? "text-white/70" : "text-muted-foreground")}>BOX {boxNum}</span>
                <span className={cn("text-xs md:text-sm font-black italic", (activeBoxIndex + 1) === boxNum ? "text-white" : "text-foreground")}>{boxCounts[boxNum - 1]}</span>
              </div>
            ))}
          </div>

          <div className="w-full flex-1 relative perspective-1000 min-h-0">
            <AnimatePresence mode="wait">
              <motion.div key={currentCard.id} initial={{ scale: 0.96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 1.04, opacity: 0, y: -10 }} className="w-full h-full">
                <motion.div className={cn("relative w-full h-full preserve-3d", !isTransitioning && "cursor-pointer")} animate={{ rotateY: isFlipped ? 180 : 0 }} transition={{ duration: 0.4 }} onClick={() => { if (!isTransitioning) setIsFlipped(!isFlipped); }}>
                  <Card className={cn(
                    "absolute inset-0 backface-hidden flex items-center justify-center text-center border-none rounded-[3rem] bg-white shadow-xl transition-all duration-300",
                    (currentCard.front.image && !currentCard.front.text) ? "p-4" : "p-6"
                  )}>
                    <CardContent className="p-0 h-full w-full flex flex-col items-center justify-center overflow-hidden">
                      <div className="absolute top-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-zinc-50/80 backdrop-blur-sm border text-[10px] font-black text-zinc-400 uppercase tracking-widest z-10">Mặt trước</div>
                      {currentCard.front.image && (
                        <div className={cn(
                          "w-full rounded-2xl flex items-center justify-center transition-all duration-500",
                          currentCard.front.text 
                            ? "h-[180px] mb-6 bg-slate-50 p-2" 
                            : "flex-1 h-full min-h-0 bg-transparent p-0"
                        )}>
                          <img 
                            src={currentCard.front.image} 
                            className="max-h-full max-w-full object-contain drop-shadow-sm" 
                            alt="" 
                          />
                        </div>
                      )}
                      {currentCard.front.text && (
                        <p className={cn(
                          "font-black italic text-zinc-950 px-4", 
                          currentCard.front.image ? "text-2xl md:text-3xl" : "text-4xl md:text-5xl"
                        )}>
                          {currentCard.front.text}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className={cn(
                    "absolute inset-0 backface-hidden [transform:rotateY(180deg)] flex items-center justify-center text-center border-none rounded-[3rem] bg-indigo-50/20 backdrop-blur-sm shadow-2xl transition-all duration-300",
                    (currentCard.back.image && !currentCard.back.text) ? "p-4" : "p-8"
                  )}>
                    <CardContent className="p-0 h-full w-full flex flex-col items-center justify-center overflow-hidden">
                      <div className="absolute top-8 left-1/2 -translate-x-1/2 text-[10px] font-black tracking-widest uppercase text-primary/40 z-10">Mặt sau</div>
                      {currentCard.back.image && (
                        <div className={cn(
                          "w-full rounded-2xl flex items-center justify-center transition-all duration-500",
                          currentCard.back.text 
                            ? "h-[180px] mb-6 bg-white/50 p-2" 
                            : "flex-1 h-full min-h-0 bg-transparent p-0"
                        )}>
                          <img 
                            src={currentCard.back.image} 
                            className="max-h-full max-w-full object-contain drop-shadow-md" 
                            alt="" 
                          />
                        </div>
                      )}
                      {currentCard.back.text && (
                        <p className={cn(
                          "font-bold italic text-primary px-6", 
                          currentCard.back.image ? "text-xl md:text-2xl" : "text-3xl md:text-4xl"
                        )}>
                          {currentCard.back.text}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="w-full md:w-2/5 flex flex-col justify-end items-center pb-6 md:pb-10">
          <AnimatePresence mode="wait">
            {studyMode === 'quiz' ? (
              <div className="grid grid-cols-1 gap-3 w-full">
                {quizOptions.map((option, idx) => {
                  const markerColors = [
                    "bg-indigo-50 border-indigo-100 text-indigo-600",
                    "bg-rose-50 border-rose-100 text-rose-600",
                    "bg-emerald-50 border-emerald-100 text-emerald-600",
                    "bg-amber-50 border-amber-100 text-amber-600"
                  ];
                  const colorClass = markerColors[idx % markerColors.length];
                  
                  return (
                    <Button 
                      key={idx} 
                      variant="ghost" 
                      className={cn(
                        "h-14 md:h-16 rounded-2xl font-bold border-2 transition-all active:scale-95 shadow-sm", 
                        selectedOption 
                          ? (option === currentCard.back.text 
                              ? "bg-emerald-500 text-white border-emerald-500 shadow-emerald-200" 
                              : (selectedOption === option ? "bg-red-500 text-white border-red-500 shadow-red-200" : "opacity-40 border-transparent")) 
                          : "bg-white hover:bg-slate-50 border-slate-100/50 hover:border-slate-200"
                      )}
                      onClick={() => { 
                        if (selectedOption || isTransitioning) return; 
                        setSelectedOption(option); 
                        setIsFlipped(true); 
                        handleRate(option === currentCard.back.text ? 'good' : 'again'); 
                      }} 
                      disabled={!!selectedOption}
                    >
                      <div className="flex items-center gap-4 w-full h-full">
                        <span className={cn(
                          "w-9 h-9 md:w-10 md:h-10 rounded-xl border flex items-center justify-center text-sm md:text-base font-black transition-all",
                          selectedOption ? "bg-white/20 border-white/30 text-white" : colorClass
                        )}>
                          {idx + 1}
                        </span>
                        <span className="flex-1 text-center pr-10 truncate text-sm md:text-base tracking-tight">
                          {option}
                        </span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 w-full h-20 md:h-24">
                <Button 
                  variant="ghost" 
                  className={cn(
                    "flex flex-col h-full rounded-[1.5rem] border-2 transition-all active:scale-95", 
                    flashcardChoice === 'again' ? "bg-red-500 text-white border-red-500" : "bg-white border-red-100 text-red-500 hover:bg-red-50"
                  )} 
                  onClick={() => handleRate('again')} 
                  disabled={isTransitioning}
                >
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-widest">CHƯA THUỘC</span>
                  <span className="text-[8px] md:text-[10px] opacity-60 italic font-medium">LÀM LẠI NGAY</span>
                </Button>
                <Button 
                  variant="ghost" 
                  className={cn(
                    "flex flex-col h-full rounded-[1.5rem] border-2 transition-all active:scale-95", 
                    flashcardChoice === 'good' ? "bg-emerald-500 text-white border-emerald-500" : "bg-white border-emerald-100 text-emerald-500 hover:bg-emerald-50"
                  )} 
                  onClick={() => handleRate('good')} 
                  disabled={isTransitioning}
                >
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-widest">ĐÃ THUỘC</span>
                  <span className="text-[8px] md:text-[10px] opacity-60 italic font-medium">LÊN BOX TIẾP</span>
                </Button>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <style>{`.perspective-1000 { perspective: 1000px; } .preserve-3d { transform-style: preserve-3d; } .backface-hidden { backface-visibility: hidden; } .scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}
